import { useEffect, useId, useState } from 'react';
import {
  ApiClientError, deleteDeviceGrant, fetchDeviceGrants, putDeviceGrant, safeTeamLabel,
} from '../../api/team';
import type { DeviceGrants, GrantScope, GranteeKind } from '../../api/team';

const SCOPES: { value: GrantScope; label: string }[] = [
  { value: 'read_index', label: 'Browse index' },
  { value: 'request_transfer', label: 'Request transfers' },
  { value: 'run_compute', label: 'Run compute' },
];
// Existing tenancy role templates; the API rechecks active org membership.
const ROLES = ['owner', 'admin', 'editor', 'viewer', 'service'];

export function DeviceGrantsPanel({ deviceId }: { deviceId: string }) {
  const formId = useId();
  const [result, setResult] = useState<{ deviceId: string; data: DeviceGrants } | null>(null);
  const [kind, setKind] = useState<GranteeKind>('member');
  const [granteeId, setGranteeId] = useState('');
  const [scopes, setScopes] = useState<GrantScope[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const data = result?.deviceId === deviceId ? result.data : null;

  useEffect(() => {
    let active = true;
    setResult(null);
    setError('');
    setGranteeId('');
    setScopes([]);
    setKind('member');
    void fetchDeviceGrants(deviceId).then((response) => {
      if (active) setResult({ deviceId, data: response });
    }).catch((failure: unknown) => {
      if (!active) return;
      setError(failure instanceof ApiClientError && failure.status === 403
        ? 'Team access is read-only and grants are unavailable for this account.'
        : 'Team access could not be loaded.');
    });
    return () => { active = false; };
  }, [deviceId]);

  async function mutate(operation: () => Promise<unknown>) {
    if (!data?.can_grant || busy) return;
    setBusy(true);
    setError('');
    try {
      await operation();
      const updated = await fetchDeviceGrants(deviceId);
      setResult({ deviceId, data: updated });
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setResult((current) => current ? { ...current, data: { ...current.data, can_grant: false } } : null);
        setError('Team access is now read-only.');
      } else {
        setError('The grant could not be saved. Reload team access and try again.');
      }
    } finally { setBusy(false); }
  }

  return (
    <section className="settings-subsection" aria-label="Device team access">
      {error ? <p role="alert">{error}</p> : null}
      {!data && !error ? <p role="status">Loading team access…</p> : null}
      {data ? <>
        {data.grants.length ? <ul>
          {data.grants.map((grant) => (
            <li key={grant.grantee_kind + ':' + grant.grantee_id}>
              <strong>{safeTeamLabel(grant.grantee_label)}</strong>{' · '}{grant.grantee_kind}
              {' · '}{grant.scopes.map((scope) => SCOPES.find((item) => item.value === scope)?.label).join(', ') || 'No scopes'}
              {' · Granted by '}{safeTeamLabel(grant.granted_by_label)}
              {' · '}{new Date(grant.granted_at).toLocaleString()}
              {data.can_grant ? <button type="button" disabled={busy} onClick={() => void mutate(
                () => deleteDeviceGrant(deviceId, grant.grantee_kind, grant.grantee_id),
              )}>Remove grant</button> : null}
            </li>
          ))}
        </ul> : <p>No team grants. Device use is restricted to its owner.</p>}
        {data.can_grant ? <form onSubmit={(event) => {
          event.preventDefault();
          void mutate(() => putDeviceGrant(deviceId, { grantee_kind: kind, grantee_id: granteeId.trim(), scopes }));
        }}>
          <fieldset disabled={busy}>
            <legend>Add or replace a grant</legend>
            <label htmlFor={formId + '-kind'}>Grant to</label>
            <select id={formId + '-kind'} value={kind} onChange={(event) => {
              const next = event.target.value as GranteeKind;
              setKind(next);
              setGranteeId(next === 'role' ? 'viewer' : '');
            }}>
              <option value="member">Member</option><option value="role">Role</option>
            </select>
            <label htmlFor={formId + '-grantee'}>{kind === 'member' ? 'Member ID' : 'Role'}</label>
            {kind === 'member' ? <input id={formId + '-grantee'} value={granteeId} required autoComplete="off"
              onChange={(event) => setGranteeId(event.target.value)} /> :
              <select id={formId + '-grantee'} value={granteeId} onChange={(event) => setGranteeId(event.target.value)}>
                {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
              </select>}
            {SCOPES.map((scope) => <label key={scope.value}>
              <input type="checkbox" checked={scopes.includes(scope.value)} onChange={(event) => {
                setScopes((current) => event.target.checked ? [...current, scope.value] : current.filter((value) => value !== scope.value));
              }} />{scope.label}
            </label>)}
            <button type="submit" disabled={!granteeId.trim() || !scopes.length}>{busy ? 'Saving…' : 'Save grant'}</button>
          </fieldset>
        </form> : <p>Team access is read-only.</p>}
      </> : null}
    </section>
  );
}
